// Web push on the client (spec v2 §16). Pure decisions here, DOM/API calls at the bottom; the
// components only render what these return. iOS needs the home-screen app before push exists.
export type Platform = "ios" | "android" | "other";
export type PushSupport = "ready" | "needs-install" | "unsupported";
export type GateDecision = "android-install" | "ios-steps" | "unsupported" | "hidden";
export type PushPref = "granted" | "denied" | "later" | null;

export const GATE_KEY = "sg.installGate";
export const PUSH_KEY = "sg.push";
export const INSTALLED_KEY = "sg.installed";
const DISMISS_GAP_MS = 3 * 86_400_000;
const MAX_DISMISSALS = 3;

export function platformOf(ua: string): Platform {
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "other";
}
// Chrome/Firefox/Edge on iPhone cannot add to the home screen, so they get the "open in Safari" line too.
export const isInAppBrowser = (ua: string): boolean => /WhatsApp|Instagram|FBAN|FBAV|Line\/|CriOS|FxiOS|EdgiOS/i.test(ua);

export function isStandalone(w: Window = window): boolean {
  const nav = w.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || (typeof w.matchMedia === "function" && w.matchMedia("(display-mode: standalone)").matches);
}

export function pushSupport(p: { platform: Platform; standalone: boolean; hasPushManager: boolean; hasNotification: boolean }): PushSupport {
  if (!p.hasPushManager || !p.hasNotification) return "unsupported";
  if (p.platform === "ios" && !p.standalone) return "needs-install";
  return "ready";
}

export function installGateDecision(p: {
  platform: Platform; standalone: boolean; inAppBrowser: boolean; dismissals: number[]; now: number; hasInstallPrompt: boolean; installed?: boolean;
}): GateDecision {
  if (p.standalone || p.installed || p.platform === "other") return "hidden";
  if (p.dismissals.length >= MAX_DISMISSALS) return "hidden";
  const last = p.dismissals.length ? Math.max(...p.dismissals) : -Infinity;
  if (p.now - last < DISMISS_GAP_MS) return "hidden";
  if (p.inAppBrowser) return "unsupported";
  if (p.platform === "android") return p.hasInstallPrompt ? "android-install" : "hidden";
  return "ios-steps";
}

const read = (k: string): string | null => { try { return localStorage.getItem(k); } catch { return null; } };
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

export function readDismissals(): number[] {
  const raw = read(GATE_KEY);
  if (!raw) return [];
  try { const a = JSON.parse(raw); return Array.isArray(a) ? a.filter((n) => typeof n === "number") : []; } catch { return []; }
}
export function recordDismissal(now = Date.now()): void { write(GATE_KEY, JSON.stringify([...readDismissals(), now])); }
// Safari cannot tell us the app is installed, so "I've added it" is remembered on the device.
export const readInstalled = (): boolean => read(INSTALLED_KEY) === "1";
export function writeInstalled(): void { write(INSTALLED_KEY, "1"); }
export function readPushPref(): PushPref { const v = read(PUSH_KEY); return v === "granted" || v === "denied" || v === "later" ? v : null; }
export function writePushPref(p: Exclude<PushPref, null>): void { write(PUSH_KEY, p); }

export function urlBase64ToUint8Array(s: string): Uint8Array {
  const padded = (s + "=".repeat((4 - (s.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(padded);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

type SubJson = { endpoint: string; keys: { p256dh: string; auth: string } };
const toJson = (s: PushSubscription): SubJson => {
  const j = s.toJSON();
  if (!j.endpoint || !j.keys?.p256dh || !j.keys?.auth) throw new Error("Subscription is missing keys");
  return { endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } };
};

export async function currentSubscription(): Promise<SubJson | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  return sub ? toJson(sub) : null;
}

/** Removes the browser's own subscription so the next open does not re-save it. Returns its endpoint, or null if none. */
export async function unsubscribeFromPush(): Promise<string | null> {
  if (!("serviceWorker" in navigator)) return null;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return null;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  return endpoint;
}

/** Call from a tap. Throws PermissionDenied only for a real "denied"; a closed prompt ("default") throws PermissionDismissed. */
export async function subscribeToPush(vapidPublicKey: string): Promise<SubJson> {
  const perm = await Notification.requestPermission();
  if (perm === "denied") throw Object.assign(new Error("Notifications are off for this site"), { name: "PermissionDenied" });
  if (perm !== "granted") throw Object.assign(new Error("Permission prompt was closed"), { name: "PermissionDismissed" });
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource });
  return toJson(sub);
}
